import { IonPage, IonContent, IonIcon } from '@ionic/react';
import React, { useState, useEffect } from 'react';
import { eyeOutline, eyeOffOutline } from 'ionicons/icons';
import { YummyText } from '../../../components/YummyText';
import Button from '../../../components/Button';
import { useHistory } from 'react-router-dom';
import { login, logout } from '../../../utils/authApi';
import { setCookie, setJSONCookie } from '../../../utils/cookies';
import { getCookie, deleteCookie } from '../../../utils/cookies';

const RiderSignIn = () => {
  const history = useHistory();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });

  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showVerifiedBanner, setShowVerifiedBanner] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Resend Verification State
  const [showResend, setShowResend] = useState(false);
  const [unverifiedUserId, setUnverifiedUserId] = useState(null);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    const needsLogin = getCookie('emailVerifiedNeedsLogin') === 'true';
    const verifiedEmail = getCookie('verifiedEmail');

    if (needsLogin && verifiedEmail) {
      setShowVerifiedBanner(true);
      deleteCookie('emailVerifiedNeedsLogin');
      deleteCookie('verifiedEmail');
      setTimeout(() => {
        setShowVerifiedBanner(false);
      }, 5000);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.email || !formData.password) {
      setError('Please enter both email and password');
      return;
    }

    setIsLoading(true);


    try {
      const response = await login({
        email: formData.email,
        password: formData.password,
        role: 'driver'
      });

      console.log('[RiderLogin] ✓ Login successful', response);

      // Check for role mismatch (Customers shouldn't login here)
      const currentUserRole = (
        (response?.user?.role || response?.data?.user?.role || response?.data?.role) ||
        // If not in response, check cookies as last resort
        (document.cookie.split('; ').find(row => row.startsWith('userRole='))?.split('=')[1]) ||
        (document.cookie.split('; ').find(row => row.startsWith('user_type='))?.split('=')[1]) ||
        'driver'
      ).toString().toLowerCase();

      if (currentUserRole !== 'driver' && currentUserRole !== 'rider' && currentUserRole !== 'admin') {
        console.warn('[RiderLogin] ⚠️ Customer attempted login on rider page');

        await logout(); // Clear cookies

        setError(
          <span>
            Access Denied. You are a Customer.<br />
            <a href="/auth/customer/login" className="text-[#00B75A] font-medium underline mt-1 block">
              Click here to go to Customer Login
            </a>
          </span>
        );
        setIsLoading(false);
        return;
      }

      // Clear verified email cookie after successful login
      deleteCookie('verifiedEmail');

      // Dispatch login event to trigger active status
      window.dispatchEvent(new CustomEvent('user:login'));

      // Redirect to dashboard
      history.push('/rider/dashboard');

    } catch (error) {
      console.error('[RiderLogin] ❌ Login failed:', error);
      const message = error?.message || 'Login failed. Please check your credentials';
      setError(message);

      // Handle unverified email error
      if (error?.status === 403 && (message.toLowerCase().includes('verify') || message.toLowerCase().includes('email'))) {
        console.log('[RiderLogin] User email not verified');

        // Store email/userId for resending
        // Check both nested data (standard) and direct property (fallback)
        const userId = error.data?.data?.userId || error.data?.userId;

        if (userId) {
          setUnverifiedUserId(userId);
          console.log('[RiderLogin] Captured unverified userId:', userId);
        } else {
          console.warn('[RiderLogin] Could not capture userId from error response:', error.data);
        }
        setUnverifiedEmail(formData.email);
        setShowResend(true);
      }
    } finally {
      setIsLoading(false);
    }
  };



  const handleResendVerification = async () => {
    if (!unverifiedUserId) {
      history.push('/auth/verify-email');
      return;
    }

    try {
      setResendLoading(true);
      const { resendVerification } = await import('../../../utils/authApi');
      await resendVerification({ userId: unverifiedUserId });

      // Show success message somehow (maybe using error state as a hack or just alert)
      setError('Verification code sent! Redirecting...');

      // Store cookie for the next page
      setCookie('pendingVerificationEmail', unverifiedEmail, 1);
      setCookie('pendingVerificationType', 'rider', 1);
      setCookie('pendingVerificationUserId', unverifiedUserId, 1);

      setTimeout(() => {
        history.push('/auth/verify-email');
      }, 1500);
    } catch (err) {
      console.error('Resend failed', err);
      setError('Failed to resend code. Please try again.');
      setResendLoading(false); // Only stop loading on error
    }
    // Do not stop loading on success, let it persist until redirect
  };

  const handleChange = (field, value) => {
    setError(''); // Clear error when user makes changes
    setFormData({
      ...formData,
      [field]: value
    });
  };

  const handleForgotPassword = () => {
    history.push('/forgot-password?role=rider');
  };

  const handleCreateAccount = () => {
    if (document && document.activeElement) document.activeElement.blur();
    history.push('/auth/rider/signup');
  };

  // Google OAuth start (redirect to backend which begins OAuth handshake)
  const rawBase = import.meta.env.VITE_API_BASE_URL || 'https://api.swiftlyxpress.com';
  const apiBase = (typeof window !== 'undefined' && rawBase.startsWith('/'))
    ? `${window.location.origin.replace(/\/$/, '')}${rawBase.replace(/\/$/, '')}`
    : rawBase.replace(/\/$/, '');

  const handleGoogleLogin = (e) => {
    e.preventDefault();
    try {
      setGoogleLoading(true);
      const googleUrl = `${apiBase}/api/auth/google?role=rider`;
      console.log('[RiderLogin] Redirecting to:', googleUrl);
      setTimeout(() => {
        window.location.href = googleUrl;
      }, 200);
    } catch (err) {
      console.error('[RiderLogin] failed to start Google OAuth', err);
      setGoogleLoading(false);
    }
  };

  return (
    <IonPage>
      <IonContent className="ion-no-padding" scrollY={false}>
        <div className="bg-white grid grid-cols-1 lg:grid-cols-2 h-screen justify-between mx-auto py-6 lg:py-8 px-4 gap-4 overflow-hidden">
          {/* Left Side - Form */}
          <div className="flex items-center justify-center lg:pr-2 lg:pl-6">
            <div className="w-full max-w-xl">
              {/* Header */}
              <div className="mb-4">
                <YummyText className="text-[#00D68F] text-[12px] font-medium mb-1">
                  Hero On Wheels
                </YummyText>
                <YummyText className="text-4xl font-[400] text-[#111827] mb-2">
                  Sign In
                </YummyText>
                <YummyText className="text-sm text-[#6B7280] leading-tight">
                  Log in to view assigned deliveries, update your status, and keep<br />
                  customers moving — one delivery at a time.
                </YummyText>
              </div>

              {/* Success Banner for Verified Email */}
              {showVerifiedBanner && (
                <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded-full animate-slide-in">
                  <YummyText className="text-xs text-green-600">✓ Email verified successfully! Please log in to continue.</YummyText>
                </div>
              )}

              {error && (
                <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-lg text-left">
                  <div className="text-xs text-red-600 leading-normal">{error}</div>
                </div>
              )}

              {/* Form */}
              <div className="space-y-2">
                {/* Email */}
                <YummyText>
                  <div>
                    <YummyText className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </YummyText>

                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="Enter your email"
                      className="w-full px-3 py-2 bg-white mb-3 border border-[#00D68F] text-xs rounded-full focus:outline-none focus:border-[#00D68F] focus:ring-0 placeholder-[#9CA3AF]"
                      style={{ fontFamily: 'inherit' }}
                    />
                  </div>

                  {/* Password */}
                  <div className="relative">
                    <YummyText className="block text-xs font-medium text-gray-700 mb-1">
                      Password
                    </YummyText>
                    <input
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      placeholder="***********"
                      className="w-full px-3 py-1 bg-white border border-[#00D68F] rounded-full focus:outline-none focus:border-[#00D68F] focus:ring-0 placeholder-[#9CA3AF]"
                      style={{ fontFamily: 'inherit' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-10 transform -translate-y-1/2 text-gray-500"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <IonIcon icon={showPassword ? eyeOffOutline : eyeOutline} className="w-5 h-5 text-[#1E1E1E]" />
                    </button>
                  </div>
                </YummyText>

                {/* Remember Me & Forgot Password */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={formData.rememberMe}
                      onChange={(e) => handleChange('rememberMe', e.target.checked)}
                      className="w-3.5 h-3.5 text-green-600 bg-white border-gray-300 mb-2 !rounded-2xl focus:ring-1 focus:ring-green-500 focus:ring-offset-0 cursor-pointer accent-green-600"
                      style={{ accentColor: '#00D68F', outline: 'none' }}
                    />
                    <YummyText className="text-xs text-gray-700 mb-2">
                      Remember me
                    </YummyText>
                  </div>
                  <span
                    onClick={handleForgotPassword}
                    className="text-xs text-[#00D68F] cursor-pointer mb-2"
                  >
                    <YummyText>forgot password?</YummyText>
                  </span>
                </div>

                {/* Log In Button */}
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="!w-full !py-2.5 !bg-[#00B75A] hover:!bg-[#00B876] !mb-2 !text-white rounded-full transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <YummyText className="font-[400] text-sm">{isLoading ? 'Logging in...' : 'Log In'}</YummyText>
                </Button>

                {/* Resend Verification Button - Only shown when needed */}
                {showResend && (
                  <Button
                    variant="primary" // Reusing primary variant but customized style
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    className="!w-full !py-2 !bg-white !border !border-[#00B75A] !text-[#00B75A] hover:!bg-green-50 !mb-2 rounded-full transition-all duration-300"
                  >
                    <YummyText className="font-[300] text-xs">
                      {resendLoading ? 'Sending...' : 'Resend Verification Code'}
                    </YummyText>
                  </Button>
                )}

                {/* Divider */}
                <YummyText>
                  <div className="relative my-3">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-[#00D68F]"></div>
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="px-3 bg-white text-gray-500">Or Continue with email</span>
                    </div>
                  </div>
                </YummyText>

                {/* Google Button */}
                <div className={`border border-[#00D68F] !mb-3 rounded-full [&>button]:border-0 ${googleLoading ? 'pointer-events-none' : ''}`}>
                  <button onClick={handleGoogleLogin} disabled={googleLoading} className={`w-full flex items-center justify-center gap-2 py-2.5 ${googleLoading ? 'opacity-80' : ''}`}>
                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    <YummyText className="text-xs font-medium text-[#1E1E1E]">Continue with Google</YummyText>
                  </button>
                </div>

                {/* Apple Button */}
                <div className="border border-[#00D68F] !mb-2 rounded-full [&>button]:border-0">
                  <button className="w-full flex items-center justify-center gap-2 py-2.5">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                    </svg>
                    <YummyText className="!text-xs !font-medium !text-[#1E1E1E]">Continue with Apple</YummyText>
                  </button>
                </div>

                {/* Create Account Link */}
                <div className="text-center mt-3">
                  <YummyText className="text-xs font-medium text-[#1E1E1E]">
                    Don't have an account?{' '}
                    <span onClick={handleCreateAccount} className="text-[#00D68F] font-medium cursor-pointer hover:text-[#1E1E1E]">
                      Create one now
                    </span>
                  </YummyText>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Image */}
          <div className="hidden lg:flex bg-[#1E1E1E] rounded-[50px] relative ml-2 mr-6 overflow-hidden p-8 max-h-full">
            <div className="relative w-full h-full flex flex-col justify-end pb-4">
              {/* Delivery Illustration */}
              <div className="absolute top-0 left-80 transform -translate-x-1/2 w-[80%] md:w-[60%] max-h-[50vh]">
                <img
                  src="/login-despatch.svg"
                  alt="Hero on Wheels"
                  className="w-full h-auto object-contain max-h-full"
                />
              </div>

              {/* Text Content at Bottom */}
              <div className="relative z-10 text-white">
                <YummyText className="text-4xl font-[300] leading-tight mb-3">
                  Welcome Back,<br />Hero on <span className="text-[#00D68F] font-semibold">Wheels</span>
                </YummyText>
              </div>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

<style jsx>{`
  @keyframes slide-in {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .animate-slide-in {
    animation: slide-in 0.3s ease-out;
  }
`}</style>

export default RiderSignIn;